// Thin wrapper around the self-hosted Umami tracker.
//
// Loaded same-origin via the host's Caddy reverse proxy at `/u/script.js` so
// that the analytics ingest endpoint is `/u/api/send` — same origin, no CORS,
// and not on the public tracker-blocker block-lists.
//
// Tracker only attaches if `VITE_UMAMI_WEBSITE_ID` is set at build time;
// otherwise every call is a no-op so local `pnpm dev` runs cleanly.

type UmamiTrackPayload = {
  url?: string;
  referrer?: string;
  hostname?: string;
  language?: string;
  screen?: string;
  title?: string;
};

type UmamiTrackerFn = (payload: UmamiTrackPayload) => UmamiTrackPayload;

interface UmamiGlobal {
  track: {
    (): void;
    (eventName: string, data?: Record<string, unknown>): void;
    (mutator: UmamiTrackerFn): void;
  };
  identify?: (id: string | Record<string, unknown>, data?: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    umami?: UmamiGlobal;
  }
}

const SCRIPT_ID = 'llura-umami-tracker';
const SCRIPT_SRC = '/u/script.js';

const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined;

let pending: Array<(u: UmamiGlobal) => void> = [];
let scriptInjected = false;

function flushPending() {
  if (!window.umami) return;
  const u = window.umami;
  const drained = pending;
  pending = [];
  for (const fn of drained) fn(u);
}

function withUmami(fn: (u: UmamiGlobal) => void) {
  if (!websiteId) return;
  if (window.umami) {
    fn(window.umami);
    return;
  }
  pending.push(fn);
}

export function initAnalytics() {
  if (!websiteId || scriptInjected) return;
  if (document.getElementById(SCRIPT_ID)) {
    scriptInjected = true;
    return;
  }
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.defer = true;
  script.dataset.websiteId = websiteId;
  // We drive pageviews manually from the React Router listener so SPA
  // navigation is captured. Without this, Umami only fires the initial load.
  script.dataset.autoTrack = 'false';
  script.addEventListener('load', flushPending);
  document.head.appendChild(script);
  scriptInjected = true;
}

export function trackPageview(url: string) {
  withUmami((u) => {
    u.track((props) => ({ ...props, url }));
  });
}

export function trackEvent(name: string, data?: Record<string, unknown>) {
  withUmami((u) => {
    u.track(name, data);
  });
}

export function identify(id: string | Record<string, unknown>, data?: Record<string, unknown>) {
  withUmami((u) => {
    u.identify?.(id, data);
  });
}
